import React, { useState, useEffect, useCallback } from "react";
import config from "./config";
import { QueryBuilder } from "./utils/QueryBuilder";
import { CssBaseline, Paper, Box, LinearProgress, ThemeProvider } from "@material-ui/core";
import Form from "./components/Form";
import Table from "./components/Table";
import { DateTime } from "luxon";
import "./css/react-date-range.css";
import theme from './utils/themeStyles';
import { loadFeed } from './utils/feed';

const articleEndpoint = `${config.baseEndpoint}/api/companies/${config.companyID}/articles`;
const qb = new QueryBuilder();

function App() {
    const [initialised, setInitialised] = useState(false);
    const [loading, setLoading] = useState(true);
    const [constants, setConstants] = useState({});
    const [getDataError, setGetDataError] = useState(false);
    const [state, setState] = useState(() => {
        return {
            pageIndex: 0,
            searchString: '',
            startDate: undefined,
            endDate: undefined,
            category: 'all',
        };
    });

    const [data, setData] = useState({
        articleCount: 0,
        articles: [],
    });

    const getFirstArticle = () => {
        qb.setPageOrdering('order[publishedAt]', 'asc');
        qb.setItemsPerPage(1);

        return loadFeed(`${articleEndpoint}?${qb.getQueryString()}`);
    };

    const getCategories = () => loadFeed(`${config.baseEndpoint}/resources/categories.json`);

    const getCompany = () => loadFeed(`${config.baseEndpoint}/api/companies/${config.companyID}`);

    const setQuery = useCallback(() => {
        qb.setItemsPerPage(config.articlesPerPage);
        qb.setPageIndex(state.pageIndex + 1);

        if (state.searchString.length) {
            qb.setSearchString(state.searchString);
        }

        if (state.category !== '' && Number.isInteger(state.category)) {
            let codes = constants.initialCategories[state.category].code;
            if (Array.isArray(codes) && codes.length === 1) codes = [codes];
            qb.setCategory(codes);
        }

        if (state.startDate !== undefined && state.endDate !== undefined) {
            qb.setDate(state.startDate.toISO(), state.endDate.toISO());
        }
    }, [state, constants]);

    useEffect(() => {
        Promise.all([
            getCategories(),
            getFirstArticle(),
            getCompany(),
        ]).then(([categoriesData, firstArticleData, companyData]) => {
            const startDate = DateTime.fromISO(firstArticleData['hydra:member'][0]['publishedAt'], { zone: 'UTC' });
            const endDate = DateTime.now();

            setConstants({
                initialStartDate: startDate,
                initialEndDate: endDate,
                initialCategories: categoriesData,
                pdfDownloadsEnabled: companyData.pdfDownloadsEnabled
            });

            setState(s => ({ ...s, startDate: startDate, endDate: endDate }));

            setInitialised(true);
        }).catch(() => setGetDataError(true));
    }, []);

    useEffect(() => {
        setLoading(true);

        if (initialised) {
            setQuery();

            loadFeed(`${articleEndpoint}?${qb.getQueryString()}`)
                .then((data) => {
                    setData({
                        articleCount: data['hydra:totalItems'],
                        articles: data['hydra:member'],
                    });
                    setLoading(false);
                });
        }
    }, [initialised, setQuery, state]);

    return(
        <ThemeProvider theme={theme}>
            <CssBaseline />
                <Paper>
                    <Box>
                        {
                            (getDataError && <>No data</>) ||
                            (!initialised && <LinearProgress />) ||
                            <>
                                {loading && <LinearProgress />}
                                <>
                                    <Form state={state} setState={setState} constants={constants} />
                                    <Table data={data} state={state} setState={setState} constants={constants} />
                                </>
                            </>
                        }
                    </Box>
                </Paper>
        </ThemeProvider>
    );
}

export default App;
